import { Text } from '@byecode/ui'
import type { OpenPageActionConfig, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { PageStruct } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import { DEVICE_ID_OPTION, getUpstreamNodesOptions, innerTypeNameMap, LOGIN_PAGE, PAGE_LINK_OPTION, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { pageListAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type OpenPageCardProps = {
    config: OpenPageActionConfig
}

const OpenPageCard: React.FC<OpenPageCardProps> = ({ config }) => {
    const { openPageId, pathParameters, queryParameters } = config
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const page = useAtomData(
        pageListAtom,
        useCallback(
            draft => {
                if (openPageId === LOGIN_PAGE) {
                    return PageStruct({ name: '登录页面' })
                }
                return find(item => item.id === openPageId, draft)
            },
            [openPageId]
        )
    )

    const userOption = useGetUserRecordOptions()

    const { pagePathParameter, pageQueryParameter } = useMemo(() => {
        return {
            pagePathParameter: page?.parameterAndVariable?.pathParameter || [],
            pageQueryParameter: page?.parameterAndVariable?.queryParameter || []
        }
    }, [page?.parameterAndVariable?.pathParameter, page?.parameterAndVariable?.queryParameter])

    const nodeIds = useMemo(() => {
        const ids = new Set<string>()
        pathParameters?.forEach(item => {
            if (item) {
                const data = extractDataFn(item.value)

                data.nodeIds.forEach(id => {
                    ids.add(id)
                })
            }
        })

        queryParameters?.forEach(item => {
            if (item) {
                const data = extractDataFn(item.value)
                data.nodeIds.forEach(id => {
                    ids.add(id)
                })
            }
        })
        return [...ids]
    }, [pathParameters, queryParameters])

    const nodes = useNodesData(nodeIds) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: nodes,
                allUpstreamNodes: nodes
            }),
        [workflowList, dataSourceList, nodes]
    )

    const textList = useCallback(
        (data: RichTextContentProtocol | undefined) => {
            if (!data || !data.content) {
                return []
            }
            const variableList: { type: string; value: string }[] = []

            function traverseContent(content: RichTextContentProtocol) {
                if (Array.isArray(content)) {
                    content.forEach(item => {
                        if (item.type === 'text') {
                            variableList.push({ type: 'text', value: item.text })
                        } else if (item.type === 'variable' && item.attrs?.value) {
                            variableList.push({
                                type: 'variable',
                                value:
                                    textRichVariableName({
                                        dataSourceList,
                                        userOption,
                                        upstreamOptions,
                                        pageOptions: [PAGE_LINK_OPTION, DEVICE_ID_OPTION],
                                        data: item.attrs.value as VariableADTvalue
                                    }) || ''
                            })
                        }
                        if (item.content) {
                            traverseContent(item.content)
                        }
                    })
                }
            }

            if (data.type === 'doc' && data.content) {
                traverseContent(data.content)
            }
            return variableList
        },
        [dataSourceList, userOption, upstreamOptions]
    )

    const pathArgs = useMemo(
        () =>
            pagePathParameter.filter(Boolean).map(item => {
                const data = find(path => path?.id === item.id, pathParameters || [])
                return {
                    id: item.id,
                    name: item.name,
                    innerType: item.innerType,
                    text: textList(data?.value)
                }
            }),
        [pagePathParameter, pathParameters, textList]
    )

    const queryArgs = useMemo(
        () =>
            pageQueryParameter.filter(Boolean).map(item => {
                const data = find(path => path?.id === item.id, queryParameters || [])
                return {
                    id: item.id,
                    name: item.name,
                    innerType: item.innerType,
                    text: textList(data?.value)
                }
            }),
        [pageQueryParameter, queryParameters, textList]
    )

    const pageName = useMemo(() => {
        if (!openPageId) {
            return ''
        }
        if (!page) {
            return '未找到页面'
        }
        return page?.name || '未命名页面'
    }, [openPageId, page])

    return (
        <CM.CardContainer>
            <CM.CardItemContainer>
                <SC.TypeName>目标页面</SC.TypeName>
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldItemTitle>{pageName}</CM.FieldItemTitle>
                    </CM.FieldItem>
                </CM.FieldList>
            </CM.CardItemContainer>

            {pathArgs && pathArgs.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>页面路径参数</SC.TypeName>
                    <CM.FieldList>
                        {pathArgs.map(item => (
                            <SC.FieldMoreLineItem key={item.id}>
                                <SC.FieldMoreLineHeader>
                                    {item.name}[{innerTypeNameMap[item.innerType]}]：
                                </SC.FieldMoreLineHeader>
                                <SC.FieldMoreLineContent>
                                    {item.text.map((t, index) => {
                                        if (t.type === 'variable') {
                                            return (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <Text inline key={String(index)} whiteSpace="normal" color="var(--color-purple-500)">
                                                    {`${t.value}`}
                                                </Text>
                                            )
                                        }

                                        return (
                                            <Text inline key={String(index)} whiteSpace="normal">
                                                {t.value}
                                            </Text>
                                        )
                                    })}
                                </SC.FieldMoreLineContent>
                            </SC.FieldMoreLineItem>
                        ))}
                    </CM.FieldList>
                </CM.CardItemContainer>
            )}
            {queryArgs && queryArgs.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>页面查询参数</SC.TypeName>
                    <CM.FieldList>
                        {queryArgs.map(item => (
                            <SC.FieldMoreLineItem key={item.id}>
                                <SC.FieldMoreLineHeader>
                                    {item.name}[{innerTypeNameMap[item.innerType]}]：
                                </SC.FieldMoreLineHeader>
                                <SC.FieldMoreLineContent>
                                    {item.text.map((t, index) => {
                                        if (t.type === 'variable') {
                                            return (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <Text inline key={String(index)} color="var(--color-purple-500)">
                                                    {`${t.value}`}
                                                </Text>
                                            )
                                        }

                                        return (
                                            <Text inline key={String(index)}>
                                                {t.value}
                                            </Text>
                                        )
                                    })}
                                </SC.FieldMoreLineContent>
                            </SC.FieldMoreLineItem>
                        ))}
                    </CM.FieldList>
                </CM.CardItemContainer>
            )}
        </CM.CardContainer>
    )
}

export default OpenPageCard
